import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useI18n } from '../lib/i18n';
import LangSwitch from './LangSwitch';
import PasswordRules, { passwordOk } from './PasswordRules';
import PasswordInput from './PasswordInput';

// ============================================================
// POSTAVLJANJE LOZINKE — za pozivnicu (type=invite) i za zaboravljenu lozinku (type=recovery).
// Link iz e-maila nosi token_hash; token se proverava tek kad korisnik klikne "Sačuvaj",
// pa ga Gmail i drugi programi koji unapred otvaraju linkove ne mogu potrošiti.
// Radi i sa starim linkovima koji odmah prijavljuju korisnika (#access_token / ?code=).
// ============================================================

const TX = {
  sr: {
    repeat: 'Ponovite novu lozinku',
    mismatch: 'Lozinke se ne poklapaju.',
    inviteHeading: 'Postavite lozinku za svoj nalog',
    expired: 'Link je istekao ili je već iskorišćen. Zatražite novu pozivnicu od FinArt Studija, ili na stranici za prijavu kliknite „Zaboravljena lozinka“.',
    noSession: 'Link nije prepoznat. Otvorite stranicu ponovo direktno iz e-maila.',
    toLogin: 'Na stranicu za prijavu',
  },
  en: {
    repeat: 'Repeat new password',
    mismatch: 'Passwords do not match.',
    inviteHeading: 'Set a password for your account',
    expired: 'The link has expired or has already been used. Ask FinArt Studio for a new invitation, or click “Forgot password” on the login page.',
    noSession: 'The link was not recognised. Open the page again directly from the e-mail.',
    toLogin: 'Go to login',
  },
  ru: {
    repeat: 'Повторите новый пароль',
    mismatch: 'Пароли не совпадают.',
    inviteHeading: 'Установите пароль для своей учётной записи',
    expired: 'Ссылка устарела или уже была использована. Попросите FinArt Studio прислать новое приглашение или нажмите «Забыли пароль» на странице входа.',
    noSession: 'Ссылка не распознана. Откройте страницу ещё раз прямо из письма.',
    toLogin: 'На страницу входа',
  },
};

// Podaci iz linka: ?token_hash=...&type=invite|recovery, ili stari oblik #access_token / ?code=
const readLink = () => {
  const q = new URLSearchParams(window.location.search);
  const h = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const type = q.get('type') || h.get('type') || 'recovery';
  return {
    tokenHash: q.get('token_hash'),
    code: q.get('code'),
    type: type === 'invite' ? 'invite' : 'recovery',
    error: q.get('error_code') || h.get('error_code') || q.get('error') || h.get('error') || '',
  };
};

export default function ResetPassword() {
  const { t, lang } = useI18n();
  const tx = TX[lang] || TX.sr;
  const [link] = useState(readLink);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState('');
  const [done, setDone] = useState(false);

  const mismatch = repeat.length > 0 && password !== repeat;
  const canSave = passwordOk(password) && password === repeat && !saving && !link.error;

  // Obezbedi prijavu iz linka; vraća true ako je korisnik prijavljen.
  // Ključ iz linka ima prednost: pripada tačno onome kome je e-mail poslat,
  // pa se lozinka nikad ne menja nekom drugom ko je slučajno prijavljen u istom pregledaču.
  const ensureSession = async () => {
    if (link.tokenHash) {
      const { data, error: err } = await supabase.auth.verifyOtp({ token_hash: link.tokenHash, type: link.type });
      if (err || !data?.session) { setError(tx.expired); setDetail(err?.message || ''); return false; }
      return true;
    }
    if (link.code) {
      const { data, error: err } = await supabase.auth.exchangeCodeForSession(link.code);
      if (err || !data?.session) { setError(tx.expired); setDetail(err?.message || ''); return false; }
      return true;
    }
    // Stari linkovi (#access_token): Supabase je već prijavio korisnika pri otvaranju stranice
    const { data: { session } } = await supabase.auth.getSession();
    if (session) return true;
    setError(tx.noSession);
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setError('');
    setDetail('');
    setSaving(true);
    const ok = await ensureSession();
    if (!ok) { setSaving(false); return; }
    const { error: err } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (err) {
      setError(t('resetPasswordError'));
      setDetail(err.message || '');
      return;
    }
    setDone(true);
    // Ukloni ključ iz adrese i uđi u aplikaciju
    setTimeout(() => window.location.replace('/'), 1500);
  };

  return (
    <div style={styles.page}>
      {/* Izbor jezika odmah na prvom koraku (pozivnica) */}
      <LangSwitch style={{ position: 'absolute', top: 20, right: 20 }} />
      <div style={styles.card}>
        <div style={styles.logoBadge}>FA</div>
        <h1 style={styles.heading}>{link.type === 'invite' ? tx.inviteHeading : t('setNewPasswordHeading')}</h1>

        {done ? (
          <div style={styles.successBox}>{t('passwordChangedRedirect')}</div>
        ) : link.error ? (
          <>
            <div style={styles.errorBox}>{tx.expired}</div>
            <button type="button" onClick={() => window.location.replace('/')} style={styles.submitBtn}>{tx.toLogin}</button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('newPasswordShortPlaceholder')} autoComplete="new-password" ariaLabel={t('newPasswordShortPlaceholder')} />
            <PasswordRules password={password} />
            <PasswordInput value={repeat} onChange={(e) => setRepeat(e.target.value)} placeholder={tx.repeat} autoComplete="new-password" ariaLabel={tx.repeat} invalid={mismatch} />
            {mismatch && <div style={styles.fieldError}>{tx.mismatch}</div>}
            {error && (
              <div style={styles.errorBox}>
                {error}
                {detail && <div style={styles.detail}>{detail}</div>}
              </div>
            )}
            <button type="submit" disabled={!canSave} style={{ ...styles.submitBtn, opacity: canSave ? 1 : 0.5 }}>
              {saving ? t('savingEllipsisShort') : t('saveNewPasswordBtn')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { position: 'relative', minHeight: '100vh', background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: { background: 'var(--surface)', borderRadius: 10, padding: '40px 32px', maxWidth: 420, width: '100%', border: '1px solid var(--line)', boxShadow: 'var(--shadow-md)' },
  logoBadge: { width: 48, height: 48, borderRadius: 10, background: 'var(--teal-800)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, margin: '0 auto 20px' },
  heading: { fontSize: 22, marginBottom: 20, textAlign: 'center' },
  fieldError: { fontSize: 12, color: 'var(--red-600)', marginTop: -6, marginBottom: 12 },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 12, lineHeight: 1.45 },
  detail: { fontSize: 11.5, opacity: 0.8, marginTop: 6, wordBreak: 'break-word' },
  successBox: { background: 'var(--forest-100)', color: 'var(--forest-600)', borderRadius: 10, padding: '12px 14px', fontSize: 14, textAlign: 'center' },
  submitBtn: { width: '100%', background: 'var(--teal-800)', color: '#fff', border: 'none', borderRadius: 8, padding: '14px 0', fontWeight: 700, fontSize: 15 },
};
